// src/components/shared/forms/FormComponents.jsx
import React from 'react';
import { AlertCircle } from 'lucide-react';

export const FormField = ({ 
  label, 
  required = false, 
  error, 
  description, 
  children,
  className = ""
}) => (
  <div className={`space-y-2 ${className}`}>
    {typeof label === 'string' ? (
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
    ) : (
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </div>
    )}
    {description && (
      <p className="text-sm text-gray-500">{description}</p>
    )}
    {children}
    {error && (
      <div className="flex items-center gap-2 text-red-600 text-sm">
        <AlertCircle className="h-4 w-4" />
        {error}
      </div>
    )}
  </div>
);

export const TextInput = ({ value, onChange, placeholder, error, className = "", ...props }) => (
  <input
    type="text"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
      error ? 'border-red-300' : 'border-gray-300'
    } ${className}`}
    {...props}
  />
);

export const SelectInput = ({ 
  value, 
  onChange, 
  options, 
  error, 
  placeholder, 
  multiple = false,
  className = "",
  ...props 
}) => (
  <select
    value={value || ''}
    onChange={(e) => {
      if (multiple) {
        const values = Array.from(e.target.selectedOptions, option => option.value);
        onChange(values);
      } else {
        onChange(e.target.value);
      }
    }}
    multiple={multiple}
    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
      error ? 'border-red-300' : 'border-gray-300'
    } ${className}`}
    {...props}
  >
    {placeholder && !multiple && <option value="">{placeholder}</option>}
    {options.map(option => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

export const TextArea = ({ 
  value, 
  onChange, 
  placeholder, 
  rows = 3, 
  error, 
  className = "",
  ...props 
}) => (
  <textarea
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${
      error ? 'border-red-300' : 'border-gray-300'
    } ${className}`}
    {...props}
  />
);

export const Toggle = ({ 
  checked = false, 
  onChange, 
  label, 
  description,
  disabled = false 
}) => (
  <div className="flex items-start justify-between">
    <div className="flex-1">
      <div className="text-sm font-medium text-gray-700">{label}</div>
      {description && <div className="text-sm text-gray-500">{description}</div>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
        checked ? 'bg-blue-600' : 'bg-gray-200'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);